import React from "react";
import cn from "./cn";

const URL_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;
// Punctuation that ends a sentence, not the address: "see github.com/x/y." —
// a closing bracket only when the address did not open one.
const TRAILING = /[.,;:!?'"]+$/;

const trimAddress = (raw) => {
  let url = raw.replace(TRAILING, "");
  for (const [open, close] of [["(", ")"], ["[", "]"]]) {
    while (url.endsWith(close) && url.split(open).length < url.split(close).length) url = url.slice(0, -1);
  }
  return url;
};

/**
 * LinkedText — text someone typed, shown the way they typed it: line breaks
 * kept, and every web address a link that opens in a new tab.
 *
 *   <LinkedText text={assignment.description} />
 *
 * Nothing in the text is ever rendered as markup — the words are React text
 * nodes, so a `<script>` in a student's answer shows as those characters. Only
 * http(s) addresses become links; a bare `www.` one is given https.
 *
 * Props:
 *   text      — the plain text
 *   as        — the element (default "div"; "p" inside running copy)
 *   className — extra classes; the size defaults to the body step
 */
const LinkedText = ({ text, as: Tag = "div", className = "" }) => {
  const value = text == null ? "" : String(text);
  if (!value.trim()) return null;

  const parts = [];
  let cursor = 0;
  for (const match of value.matchAll(URL_PATTERN)) {
    const address = trimAddress(match[0]);
    if (!address) continue;
    if (match.index > cursor) parts.push(value.slice(cursor, match.index));
    const href = /^https?:\/\//i.test(address) ? address : `https://${address}`;
    parts.push(
      <a
        key={`${match.index}-${address}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="tw-break-all tw-text-primary tw-underline tw-underline-offset-2 hover:tw-no-underline"
      >
        {address}
      </a>
    );
    cursor = match.index + address.length;
  }
  if (cursor < value.length) parts.push(value.slice(cursor));

  return (
    <Tag className={cn("tw-m-0 tw-whitespace-pre-wrap tw-break-words tw-text-sm tw-leading-relaxed tw-text-content", className)}>
      {parts}
    </Tag>
  );
};

export default LinkedText;
